/**
 * Viewer locate protocol — shared by PdfViewer / DocxPreview / TxtPreview and
 * consumed by the workbench right rail ("AI 解读" findings). A finding carries
 * a free-form `sourceLocation.ref` (e.g. "第5条第2款") plus an AI-quoted
 * `clauseText` (原文摘录); viewers resolve the target to a scroll position.
 */

export type LocateResult = "ok" | "not-found" | "not-ready";

export interface LocateTarget {
  /** Direct page jump when the source provides a page number (rare today). */
  page?: number;
  /** Free-form source reference (e.g. "第5条第2款", a section heading). */
  ref?: string;
  /** AI-quoted verbatim excerpt — preferred over ref when both are present. */
  clauseText?: string;
}

export interface ViewerHandle {
  locate(target: LocateTarget): Promise<LocateResult>;
}

/** Strip all whitespace so CJK/Latin text from pdfjs or docx-preview matches
 * regardless of how the renderer split glyphs into spans/items. */
export function normalizeLocateText(s: string): string {
  return s.replace(/\s+/g, "");
}

/**
 * Minimum segment length after splitting on ellipsis. Filters out fragments
 * too short to be distinctive (e.g. "本条款") that would otherwise risk false
 * positives across a multi-page document.
 */
const MIN_SEGMENT_LEN = 6;

/**
 * Split an AI-quoted clause on ellipsis variants the model emits when it
 * abridges the original text:
 *   - U+2026 HORIZONTAL ELLIPSIS (`…`, often doubled as `……` in CJK)
 *   - 3+ ASCII dots (`...`)
 *   - 3+ full-width Chinese periods (`。。。`)
 *
 * Whitespace is stripped first (via normalizeLocateText), and fragments
 * shorter than MIN_SEGMENT_LEN are dropped. Returns segments in their
 * original order, preserving the abridged excerpt's sequencing.
 */
export function splitClauseSegments(needle: string): string[] {
  const normalized = normalizeLocateText(needle);
  if (!normalized) return [];
  const parts = normalized.split(/[…]{1,}|\.{3,}|。{3,}/u);
  const segments: string[] = [];
  for (const part of parts) {
    if (part.length >= MIN_SEGMENT_LEN) segments.push(part);
  }
  return segments;
}

/** First surviving segment of an AI-quoted clause, or null when the needle is
 * all ellipsis / every fragment too short. Used by viewers' whole-document
 * fallback to land on the block where a cross-block quote begins. */
export function firstClauseSegment(needle: string): string | null {
  const segs = splitClauseSegments(needle);
  return segs.length > 0 ? segs[0] : null;
}

/** Separators the model uses between several sections in a free-form `ref`
 * (e.g. "第十二章/投资后公司治理章节", "支付前承诺·限制"). Whitespace splits too. */
const REF_SEPARATORS = /[/·、，,；;]\s*|\s+/u;

/** Generic trailing nouns the model appends to a section name in `ref`
 * ("第二十一条保护性条款", "认购增资章节", "协议前言·当事人列示部分",
 * "第十六章其他规定之后或之前"). Stripping them yields the verbatim heading. */
const REF_GENERIC_SUFFIX = /(?:条款|章节|部分|之后|之前|的前后|前后|条款部分)$/u;

/** A chapter/article/section number prefix: 第X章 / 第X节 / 第X条. Lets a
 * `ref` like "第十六章其他规定之后或之前" still land on the block that STARTS
 * WITH "第十六章", which is the chapter heading itself. */
const REF_CHAPTER_PREFIX =
  /第[一二三四五六七八九十百千零0-9]+(?:章节|章|节|条|款|项)/u;

/**
 * Split a free-form source `ref` into ordered candidate lookup strings, most
 * specific first. Used by viewers' ref fallback when the AI-quoted `clauseText`
 * fails to match: the ref names the section the finding is about, so jumping to
 * that section is the honest best-effort region pointer.
 *
 * For each whitespace/separator-delimited token, yields (in order):
 *   1. the token verbatim,
 *   2. the token with generic suffixes ("章节"/"部分"/"条款"/"之后"/"之前"…)
 *      stripped one layer at a time,
 *   3. any chapter/article-number prefix embedded in it ("第二十一条").
 *
 * Tokens shorter than 2 chars are dropped. Duplicates removed, order preserved.
 */
export function splitRefParts(ref: string | null | undefined): string[] {
  if (!ref) return [];
  const raw = normalizeLocateText(ref);
  if (!raw) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (s: string) => {
    if (s.length >= 2 && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  };
  for (const token of raw.split(REF_SEPARATORS)) {
    if (!token) continue;
    push(token);
    // Strip generic suffix layers (e.g. "认购增资章节" → "认购增资";
    // "第十六章其他规定之前" → "第十六章其他规定" → "第十六章其他规定").
    let stripped = token;
    for (let i = 0; i < 3; i += 1) {
      const next = stripped.replace(REF_GENERIC_SUFFIX, "");
      if (next === stripped || next.length < 2) break;
      push(next);
      stripped = next;
    }
    // Chapter/article-number prefixes embedded in the token.
    for (const m of token.matchAll(new RegExp(REF_CHAPTER_PREFIX.source, "gu"))) {
      if (m[0]) push(m[0]);
    }
  }
  return out;
}

/**
 * Decide whether an AI-quoted `needle` matches a haystack (a PDF page's text,
 * a DOCX block's textContent, a TXT line). The needle may abridge the source
 * with ellipsis (`……`); each surviving segment must appear in haystack **in
 * order**, mirroring how the model abridged the original.
 *
 * Returns false when the needle has no qualifying segment (all ellipsis or
 * every fragment too short) so the caller can fall back to `ref` or report
 * not-found.
 */
export function clauseMatches(haystack: string, needle: string): boolean {
  const segments = splitClauseSegments(needle);
  if (segments.length === 0) return false;
  const hay = normalizeLocateText(haystack);
  let cursor = 0;
  for (const seg of segments) {
    const idx = hay.indexOf(seg, cursor);
    if (idx === -1) return false;
    cursor = idx + seg.length;
  }
  return true;
}
